import { FC, useState } from 'react';
import Drawer from '..';
import DrawerHeader from '../header';
import DrawerContent from '../content';
import { useQuery } from 'react-query';
import useViewMode from '@/hooks/use-view-mode.hook';
import useFormHandle from '@/hooks/use-form-handle.hook';
import { AdminRoleLabels, AdminRoleOptions, AdminUserModel } from '@/lib/models/admin-user.model';
import ReadonlyView from '../../form/readonly-view';
import InputControl from '../../form/input-control';
import useToast from '@/hooks/use-toast.hook';
import useModal from '@/hooks/use-modal.hook';
import DrawerButtons from '@/components/form/drawer-buttons';
import {
  createAdminUser,
  deleteAdminUser,
  getAdminUserById,
  isLastOwner,
  patchAdminUser,
} from '@/lib/services/admin-user.service';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import { QueryKey, refetchQuery } from '@/lib/query-client';
import MoreMenu from '@/components/more-menu';
import UpdateMenuItems from '@/components/more-menu/update-menu-items';
import { Form } from '@/components/form';
import { FieldError } from '@/lib/services';
import FloatLabelCard from '@/components/float-label-card';
import { createUseStyles } from 'react-jss';
import { Box, Stack } from '@mui/material';
import StatusChip from '@/components/chip/status-chip';
import { DrawerProps } from '../helpers';

interface Props extends DrawerProps {
  id: string | null;
}

const fields: ReadonlyField[] = [
  { label: 'Name', prop: 'name' },
  { label: 'Email', prop: 'email' },
  { label: 'Role', prop: 'role' },
  { label: 'Permissions', prop: 'permissions' },
];

const getReadonlyData = (data: AdminUserModel | null) => {
  if (!data) {
    return {};
  }

  const { name, email, role, conversationAccess } = data;

  return {
    name,
    email,
    role: AdminRoleLabels[role],
    permissions: (
      <Stack direction="row" justifyContent="space-between" mt={1}>
        <Box>View User Event Conversation History</Box>
        <Box>
          <StatusChip value={conversationAccess ? 'enabled' : 'disabled'} size="small" />
        </Box>
      </Stack>
    ),
  };
};

const formModel = new AdminUserModel({ conversationAccess: false });

const useStyles = createUseStyles({
  permissionsCard: {
    marginTop: 16,
    '& .MuiFormControl-root': {
      marginTop: 8,
      '& div:first-child': {
        justifyContent: 'space-between',
      },
    },
  },
});

export const AdminUserDrawer: FC<Props> = ({
  id,
  open,
  onClose,
  className,
  isTopDrawer,
  hideUpdateMenu,
}) => {
  const [userId, setUserId] = useState(id || null);
  const isNew = !userId;
  const { setMode, inWriteMode } = useViewMode(isNew ? 'add' : 'view');
  const styles = useStyles();

  const { toast, errorToast } = useToast();
  const { openModal } = useModal();

  const query = useQuery([QueryKey.AdminUserView, userId], async () => getAdminUserById(userId!), {
    enabled: !isNew,
  });
  const { data, refetch } = query;
  const adminUser = data || new AdminUserModel();

  const lastOwnerQuery = useQuery([QueryKey.AdminLastOwner, userId], async () => isLastOwner());
  const { data: lastOwner } = lastOwnerQuery;

  const handleRefresh = () => {
    refetchQuery(QueryKey.AdminUsersDataset);
  };

  const formHandle = useFormHandle(
    {
      initialValues: formModel,
      validationSchema: formModel.schema,
      validateOnChange: !isNew,
      onSubmit: async (user, { resetForm, setFieldError }) => {
        const setFieldErrors = (fieldErrors: FieldError[]) => {
          fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
        };

        if (!userId) {
          const { error, fieldErrors, model, created } = await createAdminUser(user);

          if (created) {
            resetForm({ values: user });
            toast('The admin user was created');
            setUserId(model?.id || '');
            handleRefresh();
            setMode('view');
            return;
          }

          if (fieldErrors) {
            setFieldErrors(fieldErrors);
            return;
          }
          errorToast(error || 'The admin user was not created');
          return;
        }

        // @todo editing your own account? need to update app context, etc.

        const { patched, error, fieldErrors } = await patchAdminUser(user);

        if (fieldErrors) {
          setFieldErrors(fieldErrors);
          return;
        }

        if (error || !patched) {
          errorToast(error || 'The admin user was not updated');
          return;
        }

        resetForm({ values: user });
        toast('The admin user was updated');
        refetch();
        handleRefresh();
        setMode('view');
      },
    },
    { lockTarget: 'drawer' }
  );

  const { handleSubmit, canSubmit, isSubmitting, loadData } = formHandle;

  const canDelete = adminUser.role !== 'org:owner' || !lastOwner;
  const deleteSubtitle = !canDelete ? 'You cannot delete the last owner' : '';

  const handleCancel = () => {
    if (isNew) {
      onClose(true);
      return;
    }
    setMode('view');
    loadData(adminUser);
  };

  const handleEdit = () => {
    setMode('edit');
  };

  const handleDelete = () => {
    openModal('confirm', {
      title: 'Confirm Delete',
      content: `Are you sure you want to delete ${adminUser.email}?`,
      onClose: (confirm: boolean) => {
        if (confirm) {
          deleteAdminUser(adminUser).then(({ deleted, error }) => {
            if (deleted) {
              setUserId('');
              onClose();
              toast('The admin user was deleted');
              handleRefresh();
              return;
            }

            errorToast(error || 'The admin user was not deleted');
          });
        }
      },
    });
  };

  return (
    <Drawer open={open} onClose={onClose} className={className} query={query}>
      <DrawerHeader
        onClose={onClose}
        isTopDrawer={isTopDrawer}
        menu={
          <MoreMenu menuId="admin-user-menu">
            {!isNew && !hideUpdateMenu && (
              <UpdateMenuItems
                onEdit={handleEdit}
                onDelete={handleDelete}
                canEdit={!inWriteMode}
                canDelete={canDelete && !inWriteMode}
                deleteSubtitle={deleteSubtitle}
              />
            )}
          </MoreMenu>
        }
      >
        {isNew ? 'Add Admin User' : adminUser.name}
      </DrawerHeader>
      <DrawerContent>
        <Box mt={2}>
          {!inWriteMode && (
            <ReadonlyView
              fields={fields}
              data={adminUser._props}
              onData={() => getReadonlyData(adminUser)}
            />
          )}

          {inWriteMode && (
            <Form formHandle={formHandle} query={query}>
              <InputControl name="name" label="Name" formHandle={formHandle} autoFocus />

              <InputControl name="email" label="Email" formHandle={formHandle} />

              <InputControl
                name="role"
                label="Role"
                type="select"
                options={AdminRoleOptions}
                formHandle={formHandle}
              />

              <FloatLabelCard label="Permissions" className={styles.permissionsCard}>
                <InputControl
                  name="conversationAccess"
                  label="View User Event Conversation History"
                  type="switch"
                  formHandle={formHandle}
                  size="small"
                />
              </FloatLabelCard>

              <DrawerButtons
                submit
                cancel
                isNew={isNew}
                canSubmit={canSubmit}
                canCancel={!isSubmitting}
                onSubmit={handleSubmit}
                onCancel={handleCancel}
                formHandle={formHandle}
              />
            </Form>
          )}
        </Box>
      </DrawerContent>
    </Drawer>
  );
};

export default AdminUserDrawer;
